// src/App.js
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';
import { TourProvider } from './context/TourContext';
import { PageSkeleton } from './components/LoadingSkeleton';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';

// مكونات الحماية
import PrivateRoute from './components/routing/PrivateRoute';
import AdminRoute from './components/routing/AdminRoute';

// مكون التخطيط الرئيسي للتاجر والمدير
import VendorLayout from './layouts/VendorLayout';
import AdminLayout from './layouts/AdminLayout';

// الصفحات العامة
const HomePage = lazy(() => import('./pages/HomePage'));
const ClientMenuPage = lazy(() => import('./pages/ClientMenuPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ChangePasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));

// صفحات التاجر (Vendor)
const VendorDashboardPage = lazy(() => import('./pages/vendor/DashboardPage'));
const VendorSettingsPage = lazy(() => import('./pages/vendor/SettingsPage'));
const VendorOrdersPage = lazy(() => import('./pages/vendor/OrdersPage'));
const VendorMenuPage = lazy(() => import('./pages/vendor/MenuPage'));
const VendorCustomersPage = lazy(() => import('./pages/vendor/CustomersPage'));
const POSPage = lazy(() => import('./pages/vendor/POSPage'));
const TablesPage = lazy(() => import('./pages/vendor/TablesPage'));
const StaffPage = lazy(() => import('./pages/vendor/StaffPage'));
const LoyaltyPage = lazy(() => import('./pages/vendor/LoyaltyPage'));
const KitchenDisplayPage = lazy(() => import('./pages/vendor/KitchenDisplayPage'));
const KioskManagementPage = lazy(() => import('./pages/vendor/KioskManagementPage'));
const WaiterAppPage = lazy(() => import('./pages/waiter/WaiterAppPage'));
const InventoryPage = lazy(() => import('./pages/vendor/InventoryPage'));
const SalesReportsPage = lazy(() => import('./pages/vendor/SalesReportsPage'));
const PurchaseReportsPage = lazy(() => import('./pages/vendor/PurchaseReportsPage'));
const AccountingPage = lazy(() => import('./pages/vendor/AccountingPage'));
const PurchasesPage = lazy(() => import('./pages/vendor/PurchasesPage'));
const PurchaseInvoicesListPage = lazy(() => import('./pages/vendor/PurchaseInvoicesListPage'));
const SupplierItemMovementPage = lazy(() => import('./pages/vendor/SupplierItemMovementPage'));
const PurchasePaymentsPage = lazy(() => import('./pages/vendor/PurchasePaymentsPage'));
const PurchaseReturnsPage = lazy(() => import('./pages/vendor/PurchaseReturnsPage'));
const SupplierStatementPage = lazy(() => import('./pages/vendor/SupplierStatementPage'));
const LowStockAlertsPage = lazy(() => import('./pages/vendor/LowStockAlertsPage'));
const BasicPurchaseReportsPage = lazy(() => import('./pages/vendor/BasicPurchaseReportsPage'));
const SuppliersPage = lazy(() => import('./pages/vendor/SuppliersPage'));
const AssetsPage = lazy(() => import('./pages/vendor/AssetsPage'));
const ZATCAIntegrationPage = lazy(() => import('./pages/vendor/ZATCAIntegrationPage'));
const MarketingPage = lazy(() => import('./pages/vendor/MarketingPage'));
const PromotionalScreen = lazy(() => import('./pages/public/PromotionalScreen'));
const CustomerDisplayPage = lazy(() => import('./pages/public/CustomerDisplayPage'));
const SelfKioskPage = lazy(() => import('./pages/public/SelfKioskPage'));
const TaxDeclarationPage = lazy(() => import('./pages/vendor/TaxDeclarationPage'));
const BatchesPage = lazy(() => import('./pages/vendor/BatchesPage'));
const HaccpPage = lazy(() => import('./pages/vendor/HaccpPage'));
const TrainingRecordsPage = lazy(() => import('./pages/vendor/TrainingRecordsPage'));
const VendorReputationPage = lazy(() => import('./pages/vendor/VendorReputationPage'));
const QueueManagementPage = lazy(() => import('./pages/vendor/QueueManagementPage'));
const DeliveryIntegrationPage = lazy(() => import('./pages/vendor/DeliveryIntegrationPage'));
const DeliveryStatementsPage = lazy(() => import('./pages/vendor/DeliveryStatementsPage'));
const KitchenDisplaySystem = lazy(() => import('./pages/vendor/KitchenDisplaySystem'));
const VisualFloorPlan = lazy(() => import('./pages/vendor/VisualFloorPlan'));
const AiConsultantPage = lazy(() => import('./pages/vendor/AiConsultantPage'));
const RecipeCostingPage = lazy(() => import('./pages/vendor/RecipeCostingPage'));
const BranchManagementPage = lazy(() => import('./pages/vendor/BranchManagementPage'));
const HQConsolidatedDashboard = lazy(() => import('./pages/vendor/HQConsolidatedDashboard'));
const HQAccountingCenterPage = lazy(() => import('./pages/vendor/HQAccountingCenterPage'));
const MenuOptimizationPage = lazy(() => import('./pages/vendor/MenuOptimizationPage'));
const ComplianceAIPage = lazy(() => import('./pages/vendor/ComplianceAIPage'));
const BackupPage = lazy(() => import('./pages/vendor/BackupPage'));
const PayrollPage = lazy(() => import('./pages/vendor/PayrollPage'));
const AdvancedReportsPage = lazy(() => import('./pages/vendor/AdvancedReportsPage'));
const SubscriptionPage = lazy(() => import('./pages/vendor/SubscriptionPage'));
const PaymentSettingsPage = lazy(() => import('./pages/vendor/PaymentSettingsPage'));
const AnalyticsPage = lazy(() => import('./pages/vendor/AnalyticsPage'));
const CostCenterReportsPage = lazy(() => import('./pages/vendor/CostCenterReportsPage'));
const OpeningBalancesPage = lazy(() => import('./pages/vendor/OpeningBalancesPage'));
const OpeningBalanceVoucherPage = lazy(() => import('./pages/vendor/OpeningBalanceVoucherPage'));
const InventoryForecastingPage = lazy(() => import('./pages/vendor/InventoryForecasting'));
const ProductsPage = lazy(() => import('./pages/vendor/ProductsPage'));
const WarehouseManagementPage = lazy(() => import('./pages/vendor/WarehouseManagementPage'));
const QRCodePage = lazy(() => import('./pages/vendor/QRCodePage'));
const ActivityLogsPage = lazy(() => import('./pages/vendor/ActivityLogsPage'));

const PaymentResultPage = lazy(() => import('./pages/public/PaymentResultPage'));
const PublicOrderTrackerPage = lazy(() => import('./pages/public/PublicOrderTrackerPage'));

// صفحة الخطأ 404
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// الصفحات القانونية
const PrivacyPolicyPage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import('./pages/LegalPages').then(m => ({ default: m.TermsOfServicePage })));

// صفحات المدير (Admin)
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminOrdersPage = lazy(() => import('./pages/admin/OrdersPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminPlansPage = lazy(() => import('./pages/admin/AdminPlansPage'));
const AdminRestaurantsPage = lazy(() => import('./pages/admin/AdminRestaurantsPage'));
const AdminQuotationsPage = lazy(() => import('./pages/admin/AdminQuotationsPage'));
const AdminDistributorsPage = lazy(() => import('./pages/admin/AdminDistributorsPage'));
const AdminIdeasPage = lazy(() => import('./pages/admin/AdminIdeasPage'));
const AdminLossDevelopmentPage = lazy(() => import('./pages/admin/AdminLossDevelopmentPage'));
const AdminSystemControlPage = lazy(() => import('./pages/admin/AdminSystemControlPage'));
const AdminSecurityLogsPage = lazy(() => import('./pages/admin/AdminSecurityLogsPage'));
const AdminSystemHealthPage = lazy(() => import('./pages/admin/AdminSystemHealthPage'));
const AdminApiKeysPage = lazy(() => import('./pages/admin/AdminApiKeysPage'));
const AdminUserActivityPage = lazy(() => import('./pages/admin/AdminUserActivityPage'));
const AdminMaintenanceSovereigntyPage = lazy(
    () => import('./pages/admin/AdminMaintenanceSovereigntyPage')
);
const DataSovereigntyDoc = lazy(() => import('./pages/admin/DataSovereigntyDoc'));

function App() {
    useEffect(() => {

        // Fetch public landing settings to cache system logo
        fetch('/api/landing/settings')
            .then(res => res.json())
            .then(result => {
                if (result && result.success && result.data && result.data.logo_url) {
                    localStorage.setItem('system_logo_url', result.data.logo_url);
                }
            })
            .catch(err => console.error('Failed to load system settings on boot:', err));

        // Dynamic native date inputs formatting to guarantee English Gregorian format
        const enforceDateLocales = () => {
            const dateInputs = document.querySelectorAll('input[type="date"], input[type="time"], input[type="datetime-local"], input[type="month"]');
            dateInputs.forEach(input => {
                input.setAttribute('lang', 'en-GB');
                input.setAttribute('dir', 'ltr');
            });
        };
        
        enforceDateLocales();
        const observer = new MutationObserver(enforceDateLocales);
        observer.observe(document.body, { childList: true, subtree: true });

        window.addEventListener('system_date_time_format_changed', enforceDateLocales);
        
        return () => {
            observer.disconnect();
            window.removeEventListener('system_date_time_format_changed', enforceDateLocales);
        };
    }, []);

    return (
        <Router>
            <AuthProvider>
                <ThemeProvider>
                    <TourProvider>
                        <LanguageProvider>
                            <NotificationProvider>
                                <Toaster position="top-center" reverseOrder={false} />
                                <ErrorBoundary>
                                    <Suspense fallback={<PageSkeleton />}>
                                        <Routes>
                                            {/* --- الروابط العامة (Public Routes) --- */}
                                            <Route path="/" element={<HomePage />} />
                                            <Route path="/menu" element={<ClientMenuPage />} />
                                            <Route path="/menu/:tableId" element={<ClientMenuPage />} />
                                            <Route path="/login" element={<LoginPage />} />
                                            <Route path="/register" element={<RegisterPage />} />
                                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                            <Route path="/reset-password" element={<ResetPasswordPage />} />
                                            <Route path="/change-password" element={<PrivateRoute><ChangePasswordPage /></PrivateRoute>} />
                                            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                                            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                                            <Route path="/payment/moyasar/result" element={<PaymentResultPage />} />
                                            <Route path="/order/:uuid" element={<PublicOrderTrackerPage />} />

                                            {/* --- روابط لوحة تحكم التاجر (Vendor Protected Routes) --- */}
                                            <Route
                                                path="/vendor"
                                                element={
                                                    <PrivateRoute>
                                                        <VendorLayout />
                                                    </PrivateRoute>
                                                }
                                            >
                                                <Route
                                                    index
                                                    element={<Navigate to="dashboard" replace />}
                                                />
                                                <Route path="dashboard" element={<VendorDashboardPage />} />
                                                <Route path="orders" element={<VendorOrdersPage />} />
                                                <Route path="menu" element={<VendorMenuPage />} />
                                                <Route path="customers" element={<VendorCustomersPage />} />
                                                <Route path="settings" element={<PrivateRoute permission="settings.manage"><VendorSettingsPage /></PrivateRoute>} />
                                                <Route path="tables" element={<TablesPage />} />
                                                <Route path="queue" element={<QueueManagementPage />} />
                                                <Route
                                                    path="integrations"
                                                    element={<DeliveryIntegrationPage />}
                                                />
                                                <Route
                                                    path="delivery-statements"
                                                    element={<DeliveryStatementsPage />}
                                                />
                                                <Route path="staff" element={<PrivateRoute permission="staff.manage"><StaffPage /></PrivateRoute>} />
                                                <Route
                                                    path="reputation"
                                                    element={<VendorReputationPage />}
                                                />
                                                <Route path="loyalty" element={<LoyaltyPage />} />

                                                {/* New Features Routes */}
                                                <Route path="pos" element={<POSPage />} />
                                                <Route path="kitchen" element={<KitchenDisplayPage />} />
                                                <Route path="waiter-app" element={<WaiterAppPage />} />
                                                <Route
                                                    path="kiosk-settings"
                                                    element={<KioskManagementPage />}
                                                />
                                                <Route path="inventory" element={<InventoryPage />} />
                                                <Route
                                                    path="reports/sales"
                                                    element={<SalesReportsPage />}
                                                />
                                                <Route
                                                    path="reports/purchases"
                                                    element={<PurchaseReportsPage />}
                                                />
                                                <Route
                                                    path="reports/advanced"
                                                    element={<AdvancedReportsPage />}
                                                />
                                                <Route path="purchases" element={<PurchasesPage />} />
                                                <Route
                                                    path="purchases/invoices"
                                                    element={<PurchaseInvoicesListPage />}
                                                />
                                                <Route
                                                    path="purchases/supplier-movement"
                                                    element={<SupplierItemMovementPage />}
                                                />
                                                <Route
                                                    path="purchases/payments"
                                                    element={<PurchasePaymentsPage />}
                                                />
                                                <Route
                                                    path="purchases/returns"
                                                    element={<PurchaseReturnsPage />}
                                                />
                                                <Route
                                                    path="suppliers/statement"
                                                    element={<SupplierStatementPage />}
                                                />
                                                <Route
                                                    path="inventory/alerts"
                                                    element={<LowStockAlertsPage />}
                                                />
                                                <Route
                                                    path="reports/purchases-basic"
                                                    element={<BasicPurchaseReportsPage />}
                                                />
                                                <Route path="accounting" element={<AccountingPage />} />
                                                <Route
                                                    path="accounting/tax-report"
                                                    element={<TaxDeclarationPage />}
                                                />
                                                <Route path="suppliers" element={<SuppliersPage />} />
                                                <Route path="assets" element={<AssetsPage />} />
                                                <Route
                                                    path="zatca-integration"
                                                    element={<PrivateRoute permission="settings.manage"><ZATCAIntegrationPage /></PrivateRoute>}
                                                />
                                                <Route path="marketing" element={<MarketingPage />} />

                                                {/* SFDA Compliance Routes */}
                                                <Route path="batches" element={<BatchesPage />} />
                                                <Route path="haccp" element={<HaccpPage />} />
                                                <Route
                                                    path="training-records"
                                                    element={<TrainingRecordsPage />}
                                                />
                                                <Route path="kds" element={<KitchenDisplaySystem />} />
                                                <Route path="floor-plan" element={<VisualFloorPlan />} />
                                                <Route
                                                    path="ai-consultant"
                                                    element={<AiConsultantPage />}
                                                />
                                                <Route
                                                    path="menu-optimization"
                                                    element={<MenuOptimizationPage />}
                                                />
                                                <Route
                                                    path="recipe-costing"
                                                    element={<RecipeCostingPage />}
                                                />
                                                <Route
                                                    path="branch-hq"
                                                    element={<BranchManagementPage />}
                                                />
                                                <Route
                                                    path="hq-dashboard"
                                                    element={<HQConsolidatedDashboard />}
                                                />
                                                <Route
                                                    path="hq-accounting-center"
                                                    element={<HQAccountingCenterPage />}
                                                />
                                                <Route
                                                    path="compliance-ai"
                                                    element={<ComplianceAIPage />}
                                                />
                                                <Route path="backup" element={<PrivateRoute permission="settings.manage"><BackupPage /></PrivateRoute>} />
                                                <Route path="payroll" element={<PayrollPage />} />
                                                <Route path="subscription" element={<SubscriptionPage />} />
                                                <Route path="payment-settings" element={<PrivateRoute permission="settings.manage"><PaymentSettingsPage /></PrivateRoute>} />
                                                <Route path="analytics" element={<AnalyticsPage />} />
                                                <Route path="cost-center-reports" element={<CostCenterReportsPage />} />
                                                <Route path="opening-balances" element={<OpeningBalancesPage />} />
                                                <Route path="accounting/opening-balance-entry" element={<OpeningBalanceVoucherPage />} />
                                                <Route path="inventory/forecasting" element={<InventoryForecastingPage />} />
                                                <Route path="products" element={<ProductsPage />} />
                                                <Route path="warehouses" element={<WarehouseManagementPage />} />
                                                <Route path="qr-code" element={<QRCodePage />} />
                                                <Route path="activity-logs" element={<PrivateRoute permission="settings.manage"><ActivityLogsPage /></PrivateRoute>} />
                                            </Route>

                                            {/* Public Player Screen */}
                                            <Route
                                                path="/promotional-screen/:restaurantId"
                                                element={<PromotionalScreen />}
                                            />
                                            <Route
                                                path="/customer-display"
                                                element={<CustomerDisplayPage />}
                                            />
                                            <Route path="/kiosk" element={<SelfKioskPage />} />
                                            <Route path="/kiosk/:restaurantId" element={<SelfKioskPage />} />

                                            {/* --- روابط لوحة تحكم المدير (Admin Protected Routes) --- */}
                                            <Route
                                                path="/admin"
                                                element={
                                                    <AdminRoute>
                                                        <AdminLayout />
                                                    </AdminRoute>
                                                }
                                            >
                                                <Route
                                                    index
                                                    element={<Navigate to="dashboard" replace />}
                                                />
                                                <Route path="dashboard" element={<AdminDashboardPage />} />
                                                <Route path="orders" element={<AdminOrdersPage />} />
                                                <Route path="users" element={<AdminUsersPage />} />
                                                <Route path="plans" element={<AdminPlansPage />} />
                                                <Route
                                                    path="restaurants"
                                                    element={<AdminRestaurantsPage />}
                                                />
                                                <Route
                                                    path="quotations"
                                                    element={<AdminQuotationsPage />}
                                                />
                                                <Route
                                                    path="distributors"
                                                    element={<AdminDistributorsPage />}
                                                />
                                                <Route path="ideas" element={<AdminIdeasPage />} />
                                                <Route
                                                    path="loss-development"
                                                    element={<AdminLossDevelopmentPage />}
                                                />
                                                <Route
                                                    path="system-control"
                                                    element={<AdminSystemControlPage />}
                                                />
                                                <Route
                                                    path="security-logs"
                                                    element={<AdminSecurityLogsPage />}
                                                />
                                                <Route
                                                    path="system-health"
                                                    element={<AdminSystemHealthPage />}
                                                />
                                                <Route path="api-keys" element={<AdminApiKeysPage />} />
                                                <Route path="user-activity" element={<AdminUserActivityPage />} />
                                                <Route
                                                    path="maintenance-sovereignty"
                                                    element={<AdminMaintenanceSovereigntyPage />}
                                                />
                                                <Route
                                                    path="sovereignty-doc"
                                                    element={<DataSovereigntyDoc />}
                                                />
                                            </Route>

                                            {/* --- صفحة 404 --- */}
                                            <Route path="*" element={<NotFoundPage />} />
                                        </Routes>
                                    </Suspense>
                                </ErrorBoundary>
                            </NotificationProvider>
                        </LanguageProvider>
                    </TourProvider>
                </ThemeProvider>
            </AuthProvider>
        </Router>
    );
}

export default App;
